import type { FormEvent, ReactNode, RefObject } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { c } from 'ttag';

import { useGetAllEntitlements } from '@proton/account/entitlements/hooks';
import { useGetOrganization } from '@proton/account/organization/hooks';
import { useGetPaymentMethods } from '@proton/account/paymentMethods/hooks';
import { useGetUser, useUser } from '@proton/account/user/hooks';
import { Button } from '@proton/atoms/Button/Button';
import { Tooltip } from '@proton/atoms/Tooltip/Tooltip';
import { useGetCalendars } from '@proton/calendar/calendars/hooks';
import useModalState from '@proton/components/components/modalTwo/useModalState';
import PlusToPlusUpsell from '@proton/components/containers/payments/subscription/PlusToPlusUpsell';
import useAssistantFeatureEnabled from '@proton/components/hooks/assistant/useAssistantFeatureEnabled';
import useApi from '@proton/components/hooks/useApi';
import useConfig from '@proton/components/hooks/useConfig';
import useEventManager from '@proton/components/hooks/useEventManager';
import { useHandler } from '@proton/components/hooks/useHandler';
import useNotifications from '@proton/components/hooks/useNotifications';
import { useCurrencies } from '@proton/components/payments/client-extensions/useCurrencies';
import type { TelemetryPaymentFlow } from '@proton/components/payments/client-extensions/usePaymentsTelemetry';
import { useLoading } from '@proton/hooks';
import { IcGift } from '@proton/icons/icons/IcGift';
import { type CheckSubscriptionData, ProrationMode, getPaymentsVersion } from '@proton/payments/core/api/api';
import type { BillingAddress, BillingAddressExtended } from '@proton/payments/core/billing-address/billing-address';
import { getIsCustomCycle } from '@proton/payments/core/checkout';
import { getCheckoutModifiers } from '@proton/payments/core/checkout-modifiers';
import { PLANS } from '@proton/payments/core/constants';
import { DisplayablePaymentError } from '@proton/payments/core/errors';
import { captureWrongPlanIDs, captureWrongPlanName } from '@proton/payments/core/helpers';
import type {
    Currency,
    Cycle,
    FreeSubscription,
    PaymentMethodType,
    PaymentStatus,
    PlainPaymentMethodType,
    PlanIDs,
} from '@proton/payments/core/interface';
import { computeOptimisticSubscriptionMode } from '@proton/payments/core/optimisticSubscriptionMode';
import { InvalidChargebeeCardDataError } from '@proton/payments/core/payment-processors/chargebeeCardPayment';
import type { PaymentProcessorHook, PaymentProcessorType } from '@proton/payments/core/payment-processors/interface';
import type { AddonGuard } from '@proton/payments/core/plan/addons';
import {
    getIsB2BAudienceFromPlan,
    getPlanCurrencyFromPlanIDs,
    getPlanFromPlanIDs,
    getPlanNameFromIDs,
    shouldPassIsTrial as shouldPassIsTrialPayments,
} from '@proton/payments/core/plan/helpers';
import type { FreePlanDefault, Plan } from '@proton/payments/core/plan/interface';
import { hasPlanIDs, switchPlan } from '@proton/payments/core/planIDs';
import { Audience, SubscriptionMode } from '@proton/payments/core/subscription/constants';
import { getFreeCheckResult } from '@proton/payments/core/subscription/freePlans';
import {
    getAutoCoupon,
    getHas2025OfferCoupon,
    getIsB2BAudienceFromSubscription,
    getMaximumCycleForApp,
    getPlanIDs,
    hasDeprecatedVPN,
    isSubscriptionCheckForbidden,
} from '@proton/payments/core/subscription/helpers';
import type {
    FullPlansMap,
    Subscription,
    SubscriptionCheckForbiddenReason,
    SubscriptionEstimation,
} from '@proton/payments/core/subscription/interface';
import { getPlansMap } from '@proton/payments/core/subscription/plans-map-wrapper';
import { SelectedPlan } from '@proton/payments/core/subscription/selected-plan';
import { tracePaymentError } from '@proton/payments/sentry/capture';
import type { SubscriptionModificationChangeAudienceTelemetry } from '@proton/payments/telemetry/subscription-container';
import { checkoutTelemetry } from '@proton/payments/telemetry/telemetry';
import { useSubscriptionModificationChangeStepTelemetry } from '@proton/payments/telemetry/useSubscriptionModificationChangeStepTelemetry';
import { VatReverseChargeErrorModal } from '@proton/payments/ui/billing-address/containers/VatReverseChargeErrorModal';
import { useBillingAddress } from '@proton/payments/ui/billing-address/hooks/useBillingAddress';
import { PaymentsContextProvider } from '@proton/payments/ui/context/PaymentContext';
import { getStaticCouponConfig } from '@proton/payments/ui/coupon-config/get-static-coupon-config';
import { isCSCoupon } from '@proton/payments/ui/coupon-config/helpers';
import { useCouponConfig } from '@proton/payments/ui/coupon-config/useCouponConfig';
import { usePaymentPollers } from '@proton/payments/ui/hooks/usePaymentPollers';
import { CacheType } from '@proton/redux-utilities/interface';
import type { ProductParam } from '@proton/shared/lib/apps/product';
import { getShouldCalendarPreventSubscripitionChange } from '@proton/shared/lib/calendar/plans';
import { APPS } from '@proton/shared/lib/constants';
import { API_CUSTOM_ERROR_CODES } from '@proton/shared/lib/errors';
import type { Organization, UserModel } from '@proton/shared/lib/interfaces';
import isTruthy from '@proton/utils/isTruthy';
import noop from '@proton/utils/noop';

import { usePaymentFacade } from '../../../../components/payments/client-extensions';
import type { Operations, OperationsSubscriptionData } from '../../../../components/payments/react-extensions';
import { usePaymentsApi } from '../../../../components/payments/react-extensions/usePaymentsApi';
import { useModalTwoPromise } from '../../../components/modalTwo/useModalTwo';
import GenericError from '../../error/GenericError';
import { changeDefaultPaymentMethodBeforePayment } from '../DefaultPaymentMethodMessage';
import PaymentGiftCode from '../PaymentGiftCode';
import PaymentWrapper from '../PaymentWrapper';
import { PassFakeAddon } from '../planCustomizer/PassFakeAddon';
import { ProtonPlanCustomizer } from '../planCustomizer/ProtonPlanCustomizer';
import { forceAddonsMinMaxConstraints, getHasPlanCustomizer } from '../planCustomizer/helpers';
import CalendarDowngradeModal from './CalendarDowngradeModal';
import PlanSelection from './PlanSelection';
import { RenewalEnableNote } from './RenewalEnableNote';
import { useVisionaryDowngradeWarningModal } from './VisionaryDowngradeWarningModal';
import { useCancelSubscriptionFlow } from './cancelSubscription/useCancelSubscriptionFlow';
import { SubscriptionConfirmButton } from './confirm-button/SubscriptionConfirmButton';
import { SUBSCRIPTION_STEPS } from './constants';
import SubscriptionCheckoutCycleItem from './cycle-selector/SubscriptionCheckoutCycleItem';
import SubscriptionCycleSelector from './cycle-selector/SubscriptionCycleSelector';
import { type SelectedProductPlans, getDefaultSelectedProductPlans } from './helpers';
import { getAllowedCycles } from './helpers/getAllowedCycles';
import { getInitialCycle } from './helpers/getInitialCycle';
import { getInitialCheckoutStep } from './helpers/initialCheckoutStep';
import { getCommonTelemetryPayload, reportChangeTelemetry, telemetryContext } from './helpers/subscriptionTelemetry';
import { useVpn2024AddonsExperiment } from './helpers/useVpn2024AddonsExperiment';
import SubscriptionCheckout from './modal-components/SubscriptionCheckout';
import SubscriptionThanks from './modal-components/SubscriptionThanks';
import { canShowGiftCodeInput } from './modal-components/helpers/canShowGiftCodeInput';
import { showLumoAddonCustomizer } from './modal-components/helpers/showLumoAddonCustomizer';
import { showMeetAddonCustomizer } from './modal-components/helpers/showMeetAddonCustomizer';
import { PostSubscriptionModalLoadingContent } from './postSubscription/modals/PostSubscriptionModalsComponents';
import { getCodes, useSubscriptionContainerInnerCheck } from './useSubscriptionContainerInnerCheck';
import useSubscriptionModalTelemetry from './useSubscriptionModalTelemetry';

import './SubscriptionContainer.scss';

export interface Model {
    step: SUBSCRIPTION_STEPS;
    planIDs: PlanIDs;
    currency: Currency;
    cycle: Cycle;
    coupon?: string;
    gift?: string;
    initialCheckComplete: boolean;
    taxBillingAddress: BillingAddress;
    paymentForbiddenReason: SubscriptionCheckForbiddenReason;
}

const BACK: Partial<{ [key in SUBSCRIPTION_STEPS]: SUBSCRIPTION_STEPS }> = {
    [SUBSCRIPTION_STEPS.CHECKOUT]: SUBSCRIPTION_STEPS.PLAN_SELECTION,
};

/**
 * There is a problem with closures. If we use the regular useState for the preferred currency and then create
 *
 * `const plansMap = getPlansMap(plans, preferredCurrency);`
 *
 * then when currency changes we'll get the old plansMap and the optimisticCheckResult will be computed with the
 * old plansMap. That leads to situations when we have wrong price points for a given currency.
 *
 * Refs is a usual workaround to get the latest value of the variable, ignorring all the business with closures.
 *
 */
const usePlansMapRef = ({
    user,
    subscription,
    plans,
    maybeCurrency,
    paymentStatus,
    plan,
}: {
    user: UserModel;
    subscription: Subscription | FreeSubscription;
    plans: Plan[];
    maybeCurrency?: Currency;
    paymentStatus: PaymentStatus;
    plan?: PLANS;
}) => {
    const [, rerender] = useState({});

    const { getPreferredCurrency } = useCurrencies();

    const preferredCurrencyRef = useRef<Currency>(
        getPreferredCurrency({
            user,
            subscription,
            plans,
            paramCurrency: maybeCurrency,
            paymentStatus,
            paramPlanName: plan,
        })
    );

    const plansMapRef = useRef<FullPlansMap>(getPlansMap(plans, preferredCurrencyRef.current));

    return {
        plansMapRef,
        setPreferredCurrency: (currency: Currency) => {
            preferredCurrencyRef.current = currency;
            plansMapRef.current = getPlansMap(plans, currency);
            rerender({});
        },
        preferredCurrencyRef,
    };
};

interface RenderProps {
    title: string;
    content: ReactNode;
    footer?: ReactNode;
    onSubmit: (e: FormEvent) => void;
    step: SUBSCRIPTION_STEPS;
    planIDs: PlanIDs;
    model: Model;
    onModelUpdate: (updatedModel: Model) => void;
}

export interface SubscriptionContainerProps {
    topRef?: RefObject<HTMLDivElement>;
    app: ProductParam;
    step?: SUBSCRIPTION_STEPS;
    cycle?: Cycle;
    minimumCycle?: Cycle;
    maximumCycle?: Cycle;
    currency?: Currency;
    plan?: PLANS;
    planIDs?: PlanIDs;
    coupon?: string | null;
    disablePlanSelection?: boolean;
    disableThanksStep?: boolean;
    defaultAudience?: Audience;
    disableCycleSelector?: boolean;
    defaultSelectedProductPlans?: SelectedProductPlans;
    onSubscribed?: () => void;
    onUnsubscribed?: () => void;
    onCancel?: () => void;
    onCheck?: (
        data:
            | { model: Model; newModel: Model; type: 'error'; error: any }
            | { model: Model; newModel: Model; type: 'success'; result: SubscriptionEstimation }
    ) => void;
    telemetryFlow?: TelemetryPaymentFlow;
    render: (renderProps: RenderProps) => ReactNode;
    subscription: Subscription | FreeSubscription;
    organization: Organization | undefined;
    plans: Plan[];
    freePlan: FreePlanDefault;
    trial?: boolean;
    mode?: 'upsell-modal';
    upsellRef?: string;
    parent?: string;
    /**
     * If none specified, then shows all addons
     */
    allowedAddonTypes?: AddonGuard[];
    paymentStatus: PaymentStatus;
    showShortPlan?: boolean;
    // Skip plan transition check if they are handled externally
    skipPlanTransitionChecks?: boolean;
    initialBillingAddress: BillingAddress;
}

const SubscriptionContainerInner = ({
    topRef: customTopRef,
    upsellRef,
    app,
    step: maybeStep,
    cycle: maybeCycle,
    minimumCycle,
    maximumCycle: maybeMaximumCycle,
    currency: maybeCurrency,
    coupon: maybeCoupon,
    plan,
    planIDs: maybePlanIDs,
    onSubscribed,
    onUnsubscribed,
    onCancel,
    onCheck,
    disablePlanSelection,
    disableCycleSelector: maybeDisableCycleSelector,
    disableThanksStep,
    defaultAudience = Audience.B2C,
    defaultSelectedProductPlans,
    telemetryFlow,
    render,
    subscription,
    organization,
    plans,
    freePlan,
    mode,
    parent,
    allowedAddonTypes,
    paymentStatus,
    showShortPlan,
    skipPlanTransitionChecks,
    initialBillingAddress,
    trial,
}: SubscriptionContainerProps) => {
    const defaultMaximumCycle = getMaximumCycleForApp(app);
    const maximumCycle = maybeMaximumCycle ?? defaultMaximumCycle;

    const topRef = useRef<HTMLDivElement>(null);
    const api = useApi();
    const { paymentsApi } = usePaymentsApi(api);
    const [user] = useUser();
    const [loading, withLoading] = useLoading();
    const eventManager = useEventManager();
    const [calendarDowngradeModal, showCalendarDowngradeModal] = useModalTwoPromise();
    const { createNotification } = useNotifications();
    const { cancelSubscriptionModals, cancelSubscription } = useCancelSubscriptionFlow({ app });
    const getCalendars = useGetCalendars();
    const getOrganization = useGetOrganization();
    const { APP_NAME } = useConfig();
    const [subscribing, withSubscribing] = useLoading();
    const [blockCycleSelector, withBlockCycleSelector] = useLoading();
    const [blockAccountSizeSelector, withBlockAccountSizeSelector] = useLoading();
    const [loadingGift, withLoadingGift] = useLoading();
    const [subscriptionEstimationsForNonSelectedCycles, setSubscriptionEstimationsForNonSelectedCycles] =
        useState<SubscriptionEstimation[]>();
    const scribeEnabled = useAssistantFeatureEnabled();
    const [upsellModal, setUpsellModal, renderUpsellModal] = useModalState();
    const [plusToPlusUpsell, setPlusToPlusUpsell] = useState<{ unlockPlan: Plan | undefined } | null>(null);
    const getUser = useGetUser();
    const getPaymentMethods = useGetPaymentMethods();
    const getEntitlements = useGetAllEntitlements();
    const { createSubscriptionPoller } = usePaymentPollers();

    const {
        showVisionaryDowngradeWarning,
        hideVisionaryDowngradeWarning,
        visionaryDowngradeModal,
        renderVisionaryDowngradeWarningText,
    } = useVisionaryDowngradeWarningModal({ subscription });

    const { reportSubscriptionModalInitialization, reportSubscriptionModalPayment } = useSubscriptionModalTelemetry();

    const latestSubscription = subscription.UpcomingSubscription ?? subscription;

    const { plansMapRef, setPreferredCurrency, preferredCurrencyRef } = usePlansMapRef({
        user,
        subscription,
        plans,
        maybeCurrency,
        paymentStatus,
        plan,
    });

    const creditCardDetailsRef = useRef<HTMLDivElement>(null);

    const planIDs = useMemo(() => {
        const subscriptionPlanIDs = getPlanIDs(latestSubscription);

        // we don't let existing users of the deprecated VPN plan to modify their subscription
        // and stay on the same plan. If they want to do manual action then we change them to the new VPN plan.
        let newPlan = plan;
        if (hasDeprecatedVPN(latestSubscription) && !plan) {
            newPlan = PLANS.VPN2024;
        }

        if (newPlan) {
            return switchPlan({
                subscription: latestSubscription,
                newPlan,
                organization,
                plans,
            });
        }

        const planIDs = maybePlanIDs || subscriptionPlanIDs;

        return (
            forceAddonsMinMaxConstraints({
                selectedPlanIDs: planIDs,
                plansMap: plansMapRef.current,
                currency: preferredCurrencyRef.current,
                subscription,
            }) ?? planIDs
        );
    }, [subscription, organization, plans, maybePlanIDs]);

    const [model, setModel] = useState<Model>(() => {
        const step = getInitialCheckoutStep(planIDs, maybeStep);

        const currency = getPlanCurrencyFromPlanIDs(plansMapRef.current, planIDs) ?? preferredCurrencyRef.current;

        const cycle = getInitialCycle({
            cycleParam: maybeCycle,
            subscription,
            planIDs,
            plansMap: plansMapRef.current,
            isPlanSelection: step === SUBSCRIPTION_STEPS.PLAN_SELECTION,
            app,
            minimumCycle,
            maximumCycle,
            currency,
        });

        const model: Model = {
            step,
            cycle,
            currency,
            coupon: maybeCoupon || subscription.CouponCode || undefined,
            planIDs,
            initialCheckComplete: false,
            taxBillingAddress: initialBillingAddress,
            paymentForbiddenReason: { forbidden: false },
        };

        return model;
    });

    const [audience, setAudienceInner] = useState(() => {
        if ((plan && getIsB2BAudienceFromPlan(plan)) || getIsB2BAudienceFromSubscription(subscription)) {
            return Audience.B2B;
        }
        return defaultAudience;
    });

    const setAudience = (newAudience: Audience) => {
        const audienceTelemetry: SubscriptionModificationChangeAudienceTelemetry = (
            {
                [Audience.B2C]: 'b2c',
                [Audience.B2B]: 'b2b',
                [Audience.FAMILY]: 'family',
            } satisfies Record<Audience, SubscriptionModificationChangeAudienceTelemetry>
        )[newAudience];

        checkoutTelemetry.subscriptionContainer.reportAudienceChange({
            audience: audienceTelemetry,
            ...getCommonTelemetryPayload({
                user,
                subscription,
                model,
                appName: APP_NAME,
                app,
                context: telemetryContext,
            }),
        });
        setAudienceInner(newAudience);
    };

    const [checkResult, setCheckResult] = useState<SubscriptionEstimation>(
        getFreeCheckResult(model.currency, model.cycle)
    );

    const couponConfig = useCouponConfig({ checkResult, planIDs: model.planIDs, plansMap: plansMapRef.current });

    const lumoAddonEnabled = showLumoAddonCustomizer({
        subscription,
        couponConfig,
        planIDs: model.planIDs,
    });
    const meetAddonEnabled = showMeetAddonCustomizer({ couponConfig, planIDs: model.planIDs });
    const [selectedProductPlans, setSelectedProductPlans] = useState(
        defaultSelectedProductPlans ||
            getDefaultSelectedProductPlans({
                appName: app,
            })
    );

    const application = useMemo(() => {
        if (APP_NAME === APPS.PROTONVPN_SETTINGS) {
            return APPS.PROTONVPN_SETTINGS;
        }
        if (APP_NAME === APPS.PROTONACCOUNTLITE) {
            return APPS.PROTONACCOUNTLITE;
        }

        return APPS.PROTONACCOUNT;
    }, [APP_NAME]);

    const isAccountLiteApp = application === APPS.PROTONACCOUNTLITE;

    const checkoutModifiers = getCheckoutModifiers(checkResult);

    const amountDue = checkResult?.AmountDue || 0;
    const couponCode = checkResult?.Coupon?.Code;
    const couponDescription = checkResult?.Coupon?.Description;

    const subscriptionCouponCode = subscription?.CouponCode;
    const giftCodeRef = useRef<HTMLInputElement>(null);

    const amount = model.step === SUBSCRIPTION_STEPS.CHECKOUT ? amountDue : 0;

    const getCodesForSubscription = () => {
        return getCodes({
            // the gift code is always set by user directly, it can't come from subscription or from
            // /check endpoint
            gift: model.gift,
            // the coupon can come from multiple sources but must be always validated by /check
            // endpoint. If the endpoint doesn't return the code back then it's invalid and we
            // should not use it for subscription endpoint.
            coupon: checkResult?.Coupon?.Code,
        });
    };

    const isTrial = checkResult?.SubscriptionMode === SubscriptionMode.Trial;

    const handleSubscribe = async (
        operations: Operations,
        { operationsSubscriptionData, paymentMethodValue, paymentProcessorType }: SubscriptionContext
    ) => {
        if (!hasPlanIDs(operationsSubscriptionData.Plans)) {
            const result = await cancelSubscription({});
            if (result?.status === 'kept') {
                return;
            }
            onUnsubscribed?.();
            return;
        }

        const shouldCalendarPreventSubscriptionChangePromise = getShouldCalendarPreventSubscripitionChange({
            user,
            api,
            getCalendars,
            newPlan: operationsSubscriptionData.Plans,
            plans,
        });

        if (await shouldCalendarPreventSubscriptionChangePromise) {
            return showCalendarDowngradeModal();
        }

        // When user has an error during checkout, we need to return him to the exact same step
        const checkoutStep = model.step;
        try {
            const pollSubscription = createSubscriptionPoller();
            eventManager.stop();
            setModel((model) => ({ ...model, step: SUBSCRIPTION_STEPS.UPGRADE }));
            try {
                await changeDefaultPaymentMethodBeforePayment(
                    api,
                    paymentMethodValue,
                    // eslint-disable-next-line @typescript-eslint/no-use-before-define
                    paymentFacade.methods.savedMethods ?? []
                );

                const codes = getCodesForSubscription();
                await operations.subscribe({
                    Codes: codes,
                    Plans: model.planIDs,
                    Cycle: model.cycle,
                    product: app,
                    taxBillingAddress: model.taxBillingAddress,
                    StartTrial: isTrial,
                    // eslint-disable-next-line @typescript-eslint/no-use-before-define
                    vatNumber: billingAddressHook.vatNumber?.vatNumberToSubmit,
                });

                // eslint-disable-next-line @typescript-eslint/no-use-before-define
                paymentFacade.telemetry.reportPaymentSuccess(paymentProcessorType);
                if (parent === 'subscription-modal') {
                    void reportSubscriptionModalPayment({
                        cycle: model.cycle,
                        currency: model.currency,
                        plan: getPlanNameFromIDs(model.planIDs) || 'n/a',
                        coupon: model.coupon,
                    });
                }
            } catch (error) {
                // eslint-disable-next-line @typescript-eslint/no-use-before-define
                paymentFacade.telemetry.reportPaymentFailure(paymentProcessorType);

                // eslint-disable-next-line @typescript-eslint/no-use-before-define
                paymentFacade.reset();

                throw error;
            }
            await pollSubscription().catch(noop);
            // These entities may also have gotten updated, let's refetch them.
            await Promise.all([
                getUser({ cache: CacheType.None }),
                getOrganization({ cache: CacheType.None }),
                getPaymentMethods({ cache: CacheType.None }),
                getEntitlements({ cache: CacheType.None }),
            ]).catch(noop);

            if (disableThanksStep) {
                onSubscribed?.();
            } else {
                setModel((model) => ({ ...model, step: SUBSCRIPTION_STEPS.THANKS }));
            }
        } catch (error: any) {
            const { Code = 0 } = error.data || {};

            if (Code === API_CUSTOM_ERROR_CODES.PAYMENTS_SUBSCRIPTION_AMOUNT_MISMATCH) {
                await check(); // eslint-disable-line @typescript-eslint/no-use-before-define
                // translator: this message pops in a notification, in case user is waiting really too long, or does the checkout in another tab, which makes this ones not valid/expiring
                createNotification({ text: c('Error').t`Checkout expired, please try again`, type: 'error' });
            }

            setModel((model) => ({ ...model, step: checkoutStep }));
            throw error;
        } finally {
            eventManager.start();
        }
    };

    type SubscriptionContext = {
        operationsSubscriptionData: OperationsSubscriptionData;
        paymentProcessorType: PaymentProcessorType;
        paymentMethodValue: PaymentMethodType;
        paymentMethodType: PlainPaymentMethodType;
    };

    const selectedPlanCurrency = checkResult.Currency;
    const selectedPlanName = getPlanFromPlanIDs(plansMapRef.current, model.planIDs)?.Name;

    const paymentFacade = usePaymentFacade({
        checkResult,
        amount,
        currency: selectedPlanCurrency,
        selectedPlanName,
        billingAddress: model.taxBillingAddress,
        paymentStatus,
        onChargeable: (operations, { paymentProcessorType, source, sourceType }) => {
            const context: SubscriptionContext = {
                operationsSubscriptionData: {
                    Plans: model.planIDs,
                    Cycle: model.cycle,
                    product: app,
                    Codes: getCodesForSubscription(),
                    taxBillingAddress: model.taxBillingAddress,
                    StartTrial: isTrial,
                    // eslint-disable-next-line @typescript-eslint/no-use-before-define
                    vatNumber: billingAddressHook.vatNumber?.vatNumberToSubmit,
                },
                paymentProcessorType,
                paymentMethodValue: source,
                paymentMethodType: sourceType,
            };

            return withSubscribing(handleSubscribe(operations, context)).catch(noop);
        },
        flow: 'subscription',
        telemetryFlow,
        user,
        subscription,
        planIDs: model.planIDs,
        coupon: couponCode,
        isTrialIntended: Boolean(trial),
        onBeforeSepaPayment: async () => {
            if (checkResult.requestData.ProrationMode === ProrationMode.Exact) {
                const currentAmountDue = checkResult.AmountDue;
                // eslint-disable-next-line @typescript-eslint/no-use-before-define
                const newCheckResult = await check();
                if (newCheckResult?.AmountDue !== currentAmountDue) {
                    createNotification({
                        text: c('Error').t`The amount due has changed. Please try again.`,
                        type: 'warning',
                        expiration: -1,
                    });
                    return false;
                }
            }

            return true;
        },
        product: app,
        telemetryContext,
    });

    // even though this value tighly connected to paymentFacade.initialized, we still used it to *delay* the moment
    // when the loading state is hidden from the user. This is needed to avoid flickering between the moment when
    // paymentFacade.initialized === true but the first check() hasn't started yet.
    const [initialLoading, setInitialLoading] = useState(!paymentFacade.initialized);

    const isFreePlanSelected = !hasPlanIDs(model.planIDs);
    const disableCycleSelector =
        isFreePlanSelected ||
        maybeDisableCycleSelector ||
        getIsCustomCycle(model.cycle) ||
        selectedPlanName === PLANS.PASS_LIFETIME;

    const computeAllowedCycles = (planIDs: PlanIDs) =>
        getAllowedCycles({
            subscription,
            minimumCycle,
            maximumCycle,
            currency: selectedPlanCurrency,
            planIDs,
            plansMap: plansMapRef.current,
            cycleParam: maybeCycle,
            app,
            couponConfig,
        });

    /**
     * Runs subscriptionCheck for all allowed cycles, if coupon is present. That allows to display the discount for
     * all cycles at once, so it's easier for the user to compare the prices and decide what cycle is the best for them.
     */
    const estimateNonSelectedCycles = async ({
        newModel,
        checkPayload,
        checkResult,
        signal,
    }: {
        newModel: Model;
        checkPayload: CheckSubscriptionData;
        checkResult: SubscriptionEstimation;
        signal: AbortSignal;
    }) => {
        setSubscriptionEstimationsForNonSelectedCycles([]);

        const allAllowedCycles = computeAllowedCycles(newModel.planIDs);

        const additionalCycles = allAllowedCycles
            // skip the cycle that was just checked
            .filter((cycle) => cycle !== checkResult.Cycle)

            // skip cycles of the currently active subscription, because the backend doesn't allows to check them
            .filter((cycle) => !isSubscriptionCheckForbidden(subscription, { planIDs: newModel.planIDs, cycle }));

        const additionalCyclesHaveCustomBilling = additionalCycles.some((cycle) => {
            const optimisticSubscriptionMode = computeOptimisticSubscriptionMode(
                {
                    planIDs: checkPayload.Plans,
                    cycle,
                    currency: checkPayload.Currency,
                    plansMap: plansMapRef.current,
                },
                subscription
            );

            return optimisticSubscriptionMode === SubscriptionMode.CustomBillings;
        });

        const currentCycleHasCustomBilling = checkResult.SubscriptionMode === SubscriptionMode.CustomBillings;

        const hasForbiddenCheck = allAllowedCycles.some((cycle) =>
            isSubscriptionCheckForbidden(subscription, { planIDs: newModel.planIDs, cycle })
        );

        const additionalPayloads = additionalCycles.map((Cycle) => {
            const autoCoupon = getAutoCoupon({
                planIDs: newModel.planIDs,
                cycle: Cycle,
                currency: newModel.currency,
            });

            const uniqueCodes = [...new Set([...(checkPayload.Codes ?? []), autoCoupon].filter(isTruthy))];

            const payload: CheckSubscriptionData = {
                ...checkPayload,
                Cycle,
                Codes: uniqueCodes,
            };

            return payload;
        });

        const noCoupons = additionalPayloads.every((payload) => !payload.Codes || payload.Codes.length === 0);

        // In case if we have Custom Billing for the main check, then for other cycles the subscription mode will be
        // different (most likely, regular proration). Mix of custom billings and proration with coupons is practically
        // guranteed to cause incosistent UI, so it's better to just skip the additional checks.
        //
        // Additionally, if the current set of cycles has forbidden checks then it means that the user already has a
        // subscription and they just opened the subscription modification view but didn't make any changes in the
        // number of addons yet. When they add an addon, then custom billing will show up, and the UI might get
        // incosistent again. For that reason we are avoiding additional checks in this case, and simply display the
        // full pricing.
        //
        // And of course if don't have any coupon codes, we don't need to run additional checks.
        if (currentCycleHasCustomBilling || additionalCyclesHaveCustomBilling || hasForbiddenCheck || noCoupons) {
            return;
        }

        paymentsApi.cacheMultiCheck(checkPayload, checkResult);

        const additionalChecks = await paymentsApi.multiCheck(additionalPayloads, {
            signal,
            silence: true,
            plansMap: plansMapRef.current,
        });

        setSubscriptionEstimationsForNonSelectedCycles([...additionalChecks, checkResult]);
    };

    /**
     * Runs the non-essential estimations that accompany the primary check but aren't required to complete it.
     * These power optional UI affordances (e.g. showing discounts across all cycles) and are kept separate so the
     * primary check result is never blocked on them. The set of additional estimations may grow over time; right now
     * it runs {@link estimateNonSelectedCycles}.
     */
    const runAdditionalEstimations = async (
        newModel: Model,
        checkPayload: CheckSubscriptionData,
        checkResult: SubscriptionEstimation,
        signal: AbortSignal
    ) => {
        await estimateNonSelectedCycles({ newModel, checkPayload, checkResult, signal });
    };

    const shouldPassIsTrial = (newModel: Model, downgradeIsTrial: boolean) => {
        const newPlanIDs = newModel.planIDs;
        const newCycle = newModel.cycle;

        return shouldPassIsTrialPayments({
            plansMap: plansMapRef.current,
            newPlanIDs,
            newCycle,
            downgradeIsTrial,
            subscription,
            organization,
        });
    };

    const [vatReverseChargeErrorModalProps, setVatReverseChargeErrorModal, renderVatReverseChargeErrorModal] =
        useModalState();

    const { check, loadingCheck } = useSubscriptionContainerInnerCheck({
        model,
        checkResult,
        subscriptionCouponCode: subscriptionCouponCode ?? undefined,
        subscription,
        paymentsApi,
        paymentFacadeSelectedMethodType: paymentFacade.selectedMethodType,
        skipPlanTransitionChecks: skipPlanTransitionChecks ?? false,
        refs: { plansMapRef, giftCodeRef },
        setters: { setCheckResult, setModel, setVatReverseChargeErrorModal },
        callbacks: {
            runAdditionalEstimations,
            shouldPassIsTrial,
            onPlusToPlusTransition: (unlockPlan) => {
                setPlusToPlusUpsell({ unlockPlan });
                setUpsellModal(true);
            },
            onVisionaryDowngradeWarning: async () => {
                try {
                    await showVisionaryDowngradeWarning();
                } catch (e) {
                    onCancel?.();
                    throw e;
                }
            },
            onVisionaryDowngradeHide: hideVisionaryDowngradeWarning,
            onCheck,
        },
        telemetry: { APP_NAME, app, paymentFacade, user, context: telemetryContext },
    });

    useEffect(() => {
        captureWrongPlanIDs(maybePlanIDs, { source: 'SubscriptionModal/PlanIDs' });
        captureWrongPlanName(plan, { source: 'SubscriptionModal/PlanName' });

        checkoutTelemetry.reportInitialization({
            ...getCommonTelemetryPayload({
                user,
                subscription,
                model,
                appName: APP_NAME,
                app,
                context: telemetryContext,
            }),
            isTrial: Boolean(trial),
        });
    }, []);

    useSubscriptionModificationChangeStepTelemetry({
        step: (() => {
            switch (model.step) {
                case SUBSCRIPTION_STEPS.PLAN_SELECTION:
                    return 'plan_selection';
                case SUBSCRIPTION_STEPS.CHECKOUT:
                    return 'checkout';
                default:
                    return null;
            }
        })(),
        app,
    });

    useEffect(() => {
        if (!paymentFacade.initialized) {
            return;
        }

        setInitialLoading(false);

        // Trigger once to initialise the check values
        void check();
        if (parent === 'subscription-modal') {
            // Send telemetry event: initialization
            void reportSubscriptionModalInitialization({
                step: maybeStep,
                plan: plan,
                cycle: model.cycle,
                currency: model.currency,
                upsellRef,
                coupon: model.coupon,
            });
        }
    }, [paymentFacade.initialized]);

    useEffect(() => {
        // Each time the user switch between steps, scroll to the top
        if (customTopRef?.current) {
            customTopRef.current?.scrollIntoView?.();
        } else {
            topRef?.current?.scrollIntoView?.();
        }
    }, [model.step, customTopRef?.current, topRef?.current]);

    const process = async (processor?: PaymentProcessorHook) =>
        withSubscribing(async () => {
            if (!processor) {
                return;
            }

            try {
                paymentFacade.paymentContext.setSubscriptionData({
                    Plans: model.planIDs,
                    Codes: getCodesForSubscription(),
                    Cycle: model.cycle,
                    product: app,
                    taxBillingAddress: model.taxBillingAddress,
                    StartTrial: isTrial,
                    // eslint-disable-next-line @typescript-eslint/no-use-before-define
                    vatNumber: billingAddressHook.vatNumber?.vatNumberToSubmit,
                });
                await processor.processPaymentToken();
            } catch (e) {
                let tokenDidntHaveEmail = false;
                if (e instanceof DisplayablePaymentError) {
                    createNotification({ text: e.message, type: 'error' });
                    tokenDidntHaveEmail = true;
                } else if (e instanceof InvalidChargebeeCardDataError) {
                    creditCardDetailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                }

                tracePaymentError(e, {
                    tags: {
                        component: 'subscription-container',
                    },
                    extra: {
                        app,
                        step: model.step,
                        cycle: model.cycle,
                        currency: model.currency,
                        amount,
                        coupon: model.coupon,
                        planIDs,
                        audience,
                        processorType: paymentFacade.selectedProcessor?.meta.type,
                        paymentMethod: paymentFacade.selectedMethodType,
                        paymentMethodValue: paymentFacade.selectedMethodValue,
                        paymentsVersion: getPaymentsVersion(),
                        tokenDidntHaveEmail,
                    },
                });
            }
        });

    const handleChangeCycle = (cycle: Cycle) => {
        if (loadingCheck || cycle === model.cycle) {
            return;
        }

        reportChangeTelemetry(
            paymentFacade,
            { user, subscription, model, appName: APP_NAME, app, context: telemetryContext },
            {
                action: 'cycle_changed',
                selectedCycle: cycle,
            }
        );

        const checkPromise = check({ ...model, cycle });
        void withBlockAccountSizeSelector(checkPromise);
    };

    const handleGift = (gift = '') => {
        if (loadingCheck) {
            return;
        }

        reportChangeTelemetry(
            paymentFacade,
            { user, subscription, model, appName: APP_NAME, app, context: telemetryContext },
            {
                action: 'coupon_changed',
                selectedCoupon: gift,
            }
        );

        if (!gift) {
            const withoutGift = { ...model };
            delete withoutGift.gift;
            return withLoadingGift(check(withoutGift));
        }

        const uppercaseCoupon = gift.trim().toUpperCase();
        const staticCouponConfig = getStaticCouponConfig(uppercaseCoupon);
        if (
            (getHas2025OfferCoupon(uppercaseCoupon) && !isCSCoupon(uppercaseCoupon)) ||
            staticCouponConfig?.blockManualEntryOfCoupon
        ) {
            createNotification({ text: c('Error').t`Invalid code`, type: 'error' });
            return;
        }
        void withLoadingGift(check({ ...model, gift }, true));
    };

    const handleChangeCurrency = (currency: Currency, context?: { paymentMethodType: PlainPaymentMethodType }) => {
        if (loadingCheck || currency === preferredCurrencyRef.current) {
            return;
        }
        setPreferredCurrency(currency);

        const planCurrency = getPlanCurrencyFromPlanIDs(getPlansMap(plans, currency), model.planIDs) ?? currency;

        reportChangeTelemetry(
            paymentFacade,
            { user, subscription, model, appName: APP_NAME, app, context: telemetryContext },
            {
                action: 'currency_changed',
                selectedCurrency: currency,
            }
        );

        void check({ ...model, currency: planCurrency }, false, context?.paymentMethodType);
    };

    const handleBillingAddressChange = (billingAddress: BillingAddressExtended) => {
        void check({ ...model, taxBillingAddress: billingAddress });
    };

    const billingAddressHook = useBillingAddress({
        onBillingAddressChange: handleBillingAddressChange,
        initialBillingAddress,
        isAuthenticated: true,
        paymentFacade,
        telemetryContext,
        paymentsApi,
        selectedPlanName,
        onVatUpdated: () => check(),
    });

    const backStep = BACK[model.step];
    const isFreeUserWithFreePlanSelected = user.isFree && isFreePlanSelected;

    const onSubmit = (e?: FormEvent) => {
        e?.preventDefault();

        if (model.paymentForbiddenReason.forbidden) {
            onCancel?.();
            return;
        }

        if (loadingCheck || loadingGift) {
            return;
        }

        void process(paymentFacade.selectedProcessor);
    };

    const TITLE = {
        [SUBSCRIPTION_STEPS.NETWORK_ERROR]: c('Title').t`Network error`,
        [SUBSCRIPTION_STEPS.PLAN_SELECTION]: c('Title').t`Select a plan`,
        [SUBSCRIPTION_STEPS.CHECKOUT]:
            checkResult?.AmountDue === 0 && shouldPassIsTrial(model, true)
                ? c('new_plans: title').t`Review subscription`
                : c('new_plans: title').t`Review subscription and pay`,
        [SUBSCRIPTION_STEPS.UPGRADE]: '',
        [SUBSCRIPTION_STEPS.THANKS]: '',
    };

    const modeType = mode ? mode : 'modal';
    const showFreePlan = modeType === 'upsell-modal' ? false : undefined;

    const hasPaymentMethod = !!paymentFacade.methods.savedMethods?.length;

    const subscriptionCheckoutSubmit = (
        <SubscriptionConfirmButton
            currency={model.currency}
            onDone={onSubscribed}
            withLoading={withLoading}
            loading={loading || subscribing || paymentFacade.bitcoinChargebee.bitcoinLoading}
            checkResult={checkResult}
            className="w-full"
            disabled={isFreeUserWithFreePlanSelected}
            paymentForbiddenReason={model.paymentForbiddenReason}
            subscription={subscription}
            hasPaymentMethod={hasPaymentMethod}
            taxCountry={billingAddressHook.taxCountry}
            vatNumber={billingAddressHook.vatNumber}
            paymentFacade={paymentFacade}
            couponConfig={couponConfig}
            showVisionaryWarning={renderVisionaryDowngradeWarningText}
            onSubmit={onSubmit}
            app={app}
        />
    );

    const gift = canShowGiftCodeInput({
        paymentForbiddenReason: model.paymentForbiddenReason,
        couponConfig,
        checkResult,
    }) ? (
        <>
            {couponCode && (
                <div className="flex items-center mb-1">
                    <IcGift className="mr-2 mb-1" />
                    <Tooltip title={couponDescription}>
                        <code>{couponCode.toUpperCase()}</code>
                    </Tooltip>
                </div>
            )}
            <PaymentGiftCode
                giftCodeRef={giftCodeRef}
                key={
                    /* Reset the toggle state when a coupon code gets applied */
                    couponCode
                }
                giftCode={model.gift}
                onApply={handleGift}
                loading={loadingGift}
            />
        </>
    ) : null;

    const [optimisticPlanIDs, setOptimisticPlanIDs] = useState<PlanIDs | null>(null);
    const vpn2024AddonsExperiment = useVpn2024AddonsExperiment(optimisticPlanIDs ?? model.planIDs);
    const optimisticPlanIDsRef = useRef<any | undefined>();

    const handleChangePlanIDs = useHandler(
        (planIDs: PlanIDs, id: any) => {
            const newModel = { ...model, planIDs };
            setModel(newModel);
            const checkPromise = check(newModel);
            void withBlockCycleSelector(checkPromise);
            checkPromise.catch(noop).finally(() => {
                // Only if it's the latest call is it reset
                if (optimisticPlanIDsRef.current === id) {
                    setOptimisticPlanIDs(null);
                }
            });
        },
        { debounce: 300 }
    );

    const handleOptimisticPlanIDs = (planIDs: PlanIDs) => {
        const id = {};
        optimisticPlanIDsRef.current = id;
        setOptimisticPlanIDs(planIDs);
        handleChangePlanIDs(planIDs, id);
    };

    const content = (
        <>
            {!customTopRef && <div ref={topRef} />}
            {model.step === SUBSCRIPTION_STEPS.NETWORK_ERROR && <GenericError />}
            {model.step === SUBSCRIPTION_STEPS.PLAN_SELECTION && (
                <PlanSelection
                    app={app}
                    freePlan={freePlan}
                    loading={loadingCheck}
                    plans={plans}
                    currency={preferredCurrencyRef.current}
                    cycle={model.cycle}
                    maximumCycle={maximumCycle}
                    minimumCycle={minimumCycle}
                    planIDs={model.planIDs}
                    mode={modeType}
                    hasFreePlan={showFreePlan}
                    subscription={subscription}
                    onChangePlanIDs={(planIDs, cycle, currency) =>
                        check({
                            ...model,
                            planIDs,
                            cycle,
                            currency,
                            step: SUBSCRIPTION_STEPS.CHECKOUT,
                        })
                    }
                    onChangeCycle={handleChangeCycle}
                    onChangeCurrency={handleChangeCurrency}
                    onChangeAudience={setAudience}
                    audience={audience}
                    selectedProductPlans={selectedProductPlans}
                    onChangeSelectedProductPlans={setSelectedProductPlans}
                    organization={organization}
                    paymentStatus={paymentStatus}
                    paymentsApi={paymentsApi}
                    coupon={maybeCoupon ?? undefined}
                    showShortPlan={showShortPlan}
                />
            )}
            {model.step === SUBSCRIPTION_STEPS.CHECKOUT && (
                <div className="subscriptionCheckout-top-container gap-4 lg:gap-6">
                    <div className="flex-1 w-full md:w-auto pt-6">
                        <div
                            className="mx-auto max-w-custom subscriptionCheckout-soptions"
                            style={{ '--max-w-custom': '37em' }}
                        >
                            {(() => {
                                if (isFreePlanSelected) {
                                    return null;
                                }

                                const selectedPlanIDs = optimisticPlanIDs ?? model.planIDs;

                                const {
                                    displayLumo,
                                    displayMeet,
                                    displayPassAsFakeAddon,
                                    isVPNPass2023,
                                    overrideAddonsBehaviour,
                                    canDisplayAddonCustomizer,
                                } = vpn2024AddonsExperiment;

                                return (
                                    <>
                                        <h2 className="text-2xl text-bold mb-4">
                                            {couponConfig?.checkoutSubtitle
                                                ? couponConfig?.checkoutSubtitle()
                                                : c('Label').t`Subscription options`}
                                        </h2>
                                        {canDisplayAddonCustomizer && getHasPlanCustomizer(model.planIDs) && (
                                            <ProtonPlanCustomizer
                                                addonFlags={{
                                                    scribeAddonEnabled: scribeEnabled.paymentsEnabled,
                                                    lumoAddonEnabled: overrideAddonsBehaviour
                                                        ? displayLumo
                                                        : lumoAddonEnabled,
                                                    meetAddonEnabled: overrideAddonsBehaviour
                                                        ? displayMeet
                                                        : meetAddonEnabled,
                                                }}
                                                loading={blockAccountSizeSelector}
                                                currency={model.currency}
                                                cycle={model.cycle}
                                                plansMap={plansMapRef.current}
                                                selectedPlanIDs={selectedPlanIDs}
                                                onChangePlanIDs={handleOptimisticPlanIDs}
                                                latestSubscription={latestSubscription}
                                                allowedAddonTypes={allowedAddonTypes}
                                                className="subscription-container-plan-customizer"
                                                telemetryContext={telemetryContext}
                                            />
                                        )}
                                        {displayPassAsFakeAddon && (
                                            <div className="subscription-container-plan-customizer">
                                                <PassFakeAddon
                                                    currency={model.currency}
                                                    cycle={model.cycle}
                                                    plansMap={plansMapRef.current}
                                                    onAddPass={(planIDs) => {
                                                        const selectedPlan = SelectedPlan.createNormalized(
                                                            planIDs,
                                                            plansMapRef.current,
                                                            model.cycle,
                                                            model.currency
                                                        );
                                                        handleOptimisticPlanIDs(selectedPlan.planIDs);
                                                        checkoutTelemetry.reportAddPass({ context: telemetryContext });
                                                    }}
                                                    onRemovePass={() => {
                                                        const selectedPlan = SelectedPlan.createNormalized(
                                                            { [PLANS.VPN2024]: 1 },
                                                            plansMapRef.current,
                                                            model.cycle,
                                                            model.currency
                                                        );
                                                        handleOptimisticPlanIDs(selectedPlan.planIDs);
                                                    }}
                                                    vpnPassBundleSelected={isVPNPass2023}
                                                />
                                            </div>
                                        )}
                                        <div className="mb-8">
                                            {disableCycleSelector ? (
                                                <SubscriptionCheckoutCycleItem
                                                    checkResult={checkResult}
                                                    plansMap={plansMapRef.current}
                                                    planIDs={model.planIDs}
                                                    loading={loadingCheck || initialLoading}
                                                    couponConfig={couponConfig}
                                                />
                                            ) : (
                                                <SubscriptionCycleSelector
                                                    mode="buttons"
                                                    plansMap={plansMapRef.current}
                                                    planIDs={model.planIDs}
                                                    cycle={model.cycle}
                                                    currency={model.currency}
                                                    onChangeCycle={handleChangeCycle}
                                                    faded={blockCycleSelector}
                                                    additionalCheckResults={subscriptionEstimationsForNonSelectedCycles}
                                                    loading={loadingCheck || initialLoading}
                                                    allowedCycles={computeAllowedCycles(model.planIDs)}
                                                    checkResult={checkResult}
                                                />
                                            )}
                                        </div>
                                    </>
                                );
                            })()}
                            <h2 className="text-2xl text-bold mb-4">{c('Label').t`Payment details`}</h2>
                            <PaymentWrapper
                                {...paymentFacade}
                                noMaxWidth
                                hideFirstLabel={true}
                                hideSavedMethodsDetails={isAccountLiteApp}
                                onCurrencyChange={handleChangeCurrency}
                                taxCountry={billingAddressHook.taxCountry}
                                vatNumber={billingAddressHook.vatNumber}
                                subscription={subscription}
                                showTaxCountry={paymentFacade.showTaxCountry && !model.paymentForbiddenReason.forbidden}
                                creditCardDetailsRef={creditCardDetailsRef}
                            />
                            <RenewalEnableNote subscription={subscription} {...checkoutModifiers} />
                        </div>
                    </div>
                    <div className="subscriptionCheckout-column bg-weak rounded">
                        <div
                            className="subscriptionCheckout-container sticky top-0"
                            data-testid="subscription-checkout"
                        >
                            <SubscriptionCheckout
                                freePlan={freePlan}
                                subscription={subscription}
                                plansMap={plansMapRef.current}
                                checkResult={checkResult}
                                gift={gift}
                                submit={subscriptionCheckoutSubmit}
                                loading={loadingCheck || initialLoading}
                                currency={model.currency}
                                cycle={model.cycle}
                                planIDs={model.planIDs}
                                onChangeCurrency={handleChangeCurrency}
                                paymentFacade={paymentFacade}
                                paymentMethods={paymentFacade.methods}
                                showPlanDescription={audience !== Audience.B2B}
                                paymentForbiddenReason={model.paymentForbiddenReason}
                                taxCountry={billingAddressHook.taxCountry}
                                user={user}
                                couponConfig={couponConfig}
                                trial={shouldPassIsTrial(model, false)}
                                {...checkoutModifiers}
                            />
                        </div>
                    </div>
                </div>
            )}
            {model.step === SUBSCRIPTION_STEPS.UPGRADE && (
                <PostSubscriptionModalLoadingContent title={c('Info').t`Registering your subscription…`} />
            )}
            {model.step === SUBSCRIPTION_STEPS.THANKS && (
                <SubscriptionThanks
                    planIDs={model.planIDs}
                    onClose={() => {
                        onSubscribed?.();
                    }}
                />
            )}
        </>
    );

    const footer = (() => {
        if (
            !model.initialCheckComplete ||
            (disablePlanSelection && backStep === SUBSCRIPTION_STEPS.PLAN_SELECTION) ||
            backStep === undefined
        ) {
            return undefined;
        }

        return (
            <Button
                onClick={() => {
                    setModel({ ...model, step: backStep });
                }}
            >{c('Action').t`Back`}</Button>
        );
    })();

    return (
        <>
            {renderUpsellModal && plusToPlusUpsell && (
                <PlusToPlusUpsell
                    {...upsellModal}
                    unlockPlan={plusToPlusUpsell.unlockPlan}
                    plansMap={plansMapRef.current}
                    onUpgrade={() => {
                        upsellModal.onClose();
                        check({
                            ...model,
                            planIDs: switchPlan({
                                subscription: latestSubscription,
                                newPlan: PLANS.BUNDLE,
                                organization,
                                plans,
                            }),
                            step: SUBSCRIPTION_STEPS.CHECKOUT,
                        }).catch(noop);
                    }}
                    onClose={() => {
                        upsellModal.onClose();
                    }}
                />
            )}
            {visionaryDowngradeModal}
            {calendarDowngradeModal(({ onResolve, onReject, ...modalProps }) => {
                return (
                    <CalendarDowngradeModal
                        {...modalProps}
                        isDowngrade={false}
                        onConfirm={onResolve}
                        onClose={onReject}
                    />
                );
            })}
            {cancelSubscriptionModals}
            {renderVatReverseChargeErrorModal && (
                <VatReverseChargeErrorModal
                    {...vatReverseChargeErrorModalProps}
                    disableSpaNavigation={isAccountLiteApp}
                    onEditAddress={() => {
                        // Do nothing for account lite app, because we have disableSpaNavigation=true, so "Edit billing address"
                        // button is just <a> link which will redirect user to the dashboard. So basically onEditAddress
                        // callback is irrelevant for account lite app.

                        if (!isAccountLiteApp) {
                            onCancel?.();
                        }
                    }}
                    onCancel={() => {
                        if (isAccountLiteApp) {
                            // after VatReverseChargeErrorModal is closed, switch back to plan selection
                            setModel({ ...model, step: SUBSCRIPTION_STEPS.PLAN_SELECTION });
                        } else {
                            // after VatReverseChargeErrorModal is closed, close the subscription modal too
                            onCancel?.();
                        }
                    }}
                />
            )}
            {render({
                onSubmit,
                title: TITLE[model.step],
                content,
                footer,
                step: model.step,
                planIDs: model.planIDs,
                model,
                onModelUpdate: (updatedModel: Model) => setModel(updatedModel),
            })}
        </>
    );
};

const SubscriptionContainer = (props: SubscriptionContainerProps) => {
    return (
        <PaymentsContextProvider>
            <SubscriptionContainerInner {...props} />
        </PaymentsContextProvider>
    );
};

export default SubscriptionContainer;
